import { APIStatus } from "@/api/APIStatus.type";
import {
  markEvoComplete,
  markEvoUncomplete,
} from "@/api/privateRequests/sbcs/markEvolutionsComplete";
import { getEvolutions } from "@/api/publicRequests/evolutions/getEvolutions";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { IRootState } from "../store";

export type Requirement = {
  label: string;
  value: string;
};

export type Upgrade = {
  label: string;
  value: string;
};

export type Cost = {
  coins: number;
  points: number;
};

export type Evolution = {
  completed: boolean;
  id: number;
  name: string;
  description: string;
  requirements: Requirement[];
  upgrades: Upgrade[];
  endTime: number;
  newEvolution: boolean;
  processingState: 1 | 2 | 3;
  cost?: Cost;
  categoryId: number;
};

type EvolutionsState = {
  status: APIStatus;
  evolutions: Evolution[];
};

const initialState: EvolutionsState = {
  status: "idle",
  evolutions: [],
};

export const fetchEvolutions = createAsyncThunk("evolutions", async () => {
  return await getEvolutions();
});

export const markEvoCompleteThunk = createAsyncThunk(
  "mark-evo-complete",
  async ({ id, categoryId }: { id: number; categoryId: number }) => {
    markEvoComplete({ id, categoryId });
    return { id, categoryId };
  },
);

export const markEvoUncompleteThunk = createAsyncThunk(
  "mark-evo-uncomplete",
  async ({ id, categoryId }: { id: number; categoryId: number }) => {
    markEvoUncomplete({ id, categoryId });
    return { id, categoryId };
  },
);

const evolutionsSlice = createSlice({
  name: "evolutions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvolutions.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchEvolutions.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.evolutions = action.payload;
      })
      .addCase(fetchEvolutions.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(markEvoCompleteThunk.fulfilled, (state, action) => {
        state.evolutions = state.evolutions.map((evo) => {
          if (
            evo.id === action.payload.id &&
            evo.categoryId === action.payload.categoryId
          ) {
            return { ...evo, completed: true };
          }
          return evo;
        });
      })
      .addCase(markEvoUncompleteThunk.fulfilled, (state, action) => {
        state.evolutions = state.evolutions.map((evo) => {
          if (
            evo.id === action.payload.id &&
            evo.categoryId === action.payload.categoryId
          ) {
            return { ...evo, completed: false };
          }
          return evo;
        });
      });
  },
});

export const evolutionsSelector = (state: IRootState) =>
  state.evolutionsReducer;

export default evolutionsSlice.reducer;
